// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { useBranding } from "./BrandingContext";
import { toast } from "react-toastify";
import useApi from "../hook/useApi";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [subAdminLogout, setSubAdminLogout] = useState(false);
  const [adminLogout, setAdminLogout] = useState(false);
  const [profile, setProfile] = useState(null); // for admin profile
  const [loading, setLoading] = useState(true);
  const [subAdminId, setSubAdminId] = useState();
  const { AsyncPostApiCall } = useApi();
  const { branding, isMaster } = useBranding();
  const appName = branding?.appName || "ProTrade";

  useEffect(() => {
    const token =
      sessionStorage.getItem("token") || sessionStorage.getItem("token");
    const storedProfile = sessionStorage.getItem("profile");
    const hasSubAdminId = !!sessionStorage.getItem("subAdminId");
    setIsAuthenticated(!!token);
    if (storedProfile) {
      setProfile(JSON.parse(storedProfile));
    }
    if (hasSubAdminId) setSubAdminId(true);
    if (token) {
      if (hasSubAdminId) {
        document.title = `${appName} - Sub-Admin`;
      } else {
        const parsedProfile = JSON.parse(storedProfile || "{}");
        const role = parsedProfile.role || profile?.role;
        document.title = role === "SUPERADMIN" ? `${appName} - Super-Admin` : `${appName} - Admin`;
      }
    } else {
      document.title = isMaster ? `${appName} - Super-Admin` : `${appName} - Admin`;
    }
    setLoading(false);
  }, [appName]);

  const login = (token, profileData, subAdminId = "", sessionSecret = "") => {
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("profile", JSON.stringify(profileData));
    if (subAdminId) {
      sessionStorage.setItem("subAdminId", subAdminId);
      sessionStorage.setItem("sessionSecret", sessionSecret);
      setSubAdminId(true);
    }
    setIsAuthenticated(true);
    setProfile(profileData);
    setSubAdminLogout(false);
    setAdminLogout(false);
    if (subAdminId) {
      document.title = `${appName} - Sub-Admin`;
    } else {
      document.title = profileData?.role === "SUPERADMIN" ? `${appName} - Super-Admin` : `${appName} - Admin`;
    }
  };

  const logout = async (subAdminIdParam = "", sessionSecretParam = "") => {
    const isSubAdmin = subAdminIdParam || sessionStorage.getItem("subAdminId");
    const activeSecret =
      sessionSecretParam || sessionStorage.getItem("sessionSecret");

    const clearLocalSession = (isSubAdminLogout = false) => {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("profile");
      sessionStorage.removeItem("subAdminId");
      sessionStorage.removeItem("sessionSecret");
      setIsAuthenticated(false);
      if (isSubAdminLogout) {
        setSubAdminLogout(true);
        setAdminLogout(false);
      } else {
        setAdminLogout(true);
        setSubAdminLogout(false);
      }
      setProfile(null);
      setSubAdminId(false);
    };

    try {
      if (isSubAdmin) {
        const payload = {
          userId: profile?._id,
          sessionSecret: activeSecret,
        };
        const result = await AsyncPostApiCall(`/auth/logout`, payload);

        if (result?.success) {
          const isSubAdminLogout = true;
          clearLocalSession(isSubAdminLogout);

          toast.success(result?.message || "Sub Admin logged out successfully.");
          document.title = `${appName} - Sub-Admin`;
          // navigate("/sub-admin-login");
        } else {
          toast.error(
            result?.message || "Something went wrong. Please try again.",
          );
        }
      } else {
        clearLocalSession();
        toast.success("Admin logged out successfully.");
        document.title = isMaster ? `${appName} - Super-Admin` : `${appName} - Admin`;
        // navigate("/");
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      // This will call the API and clear local state
      logout();
    };
    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        profile,
        setProfile,
        loading,
        subAdminLogout,
        setSubAdminLogout,
        adminLogout,
        setAdminLogout,
        subAdminId,
        setSubAdminId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
